import '../../pages/TryoutAthletesPage.css';

import React, { useCallback, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import AthleteCard from '../../components/common/AthleteCard/AthleteCard';
import AthleteQuickSelectTabBar
  from '../../components/common/AthleteQuickSelectTabBar/AthleteQuickSelectTabBar';
import AthleteSearchBar from '../../components/common/AthleteSearchBar/AthleteSearchBar';
import PageTopNav from '../../components/common/PageTopNav/PageTopNav';
import ScrollToTopFab from '../../components/common/ScrollToTopFab/ScrollToTopFab';
import { AthletePosition, AthleteWithId } from '../../types/athlete';
import { useDemoStore } from '../store/DemoStoreContext';

type FilterValue = 'all' | AthletePosition;

const SCROLL_TOP_FAB_THRESHOLD_PX = 180;

const DemoTryoutAthletesPage: React.FC = () => {
  const navigate = useNavigate();
  const { tryoutMeta, athletes, tabbedAthletes } = useDemoStore();

  const [searchQuery, setSearchQuery] = useState('');
  const [activeFilter, setActiveFilter] = useState<FilterValue>('all');
  const [scrollTopFabVisible, setScrollTopFabVisible] = useState(false);
  const contentScrollRef = useRef<HTMLDivElement>(null);

  const handleContentScroll = useCallback((): void => {
    const el = contentScrollRef.current;
    if (!el) return;
    setScrollTopFabVisible(el.scrollTop > SCROLL_TOP_FAB_THRESHOLD_PX);
  }, []);

  const scrollContentToTop = useCallback((): void => {
    contentScrollRef.current?.scrollTo({ top: 0, behavior: 'auto' });
  }, []);

  const sortedAthletes = useMemo<AthleteWithId[]>(() => {
    return [...athletes].sort((a, b) => {
      const na = parseInt(a.number, 10);
      const nb = parseInt(b.number, 10);
      if (!isNaN(na) && !isNaN(nb)) return na - nb;
      return a.number.localeCompare(b.number);
    });
  }, [athletes]);

  const isSearching = searchQuery.trim().length > 0;

  const filteredAthletes = useMemo<AthleteWithId[]>(() => {
    let list = sortedAthletes;
    if (!isSearching && activeFilter !== 'all') {
      list = list.filter((a) => a.position === activeFilter);
    }
    const q = searchQuery.trim().toLowerCase();
    if (q) {
      list = list.filter(
        (a) => a.name.toLowerCase().includes(q) || a.number.toLowerCase().includes(q),
      );
    }
    return list;
  }, [sortedAthletes, activeFilter, searchQuery, isSearching]);

  const quickAccessAthletes = useMemo<AthleteWithId[]>(() => {
    if (tabbedAthletes.length === 0) return [];
    const byId = new Map(sortedAthletes.map((a) => [a.id, a]));
    return tabbedAthletes
      .map((id) => byId.get(id))
      .filter((a): a is AthleteWithId => Boolean(a));
  }, [tabbedAthletes, sortedAthletes]);
  const quickAccessAthleteIdSet = useMemo(
    () => new Set(quickAccessAthletes.map((a) => a.id)),
    [quickAccessAthletes],
  );
  const hasQuickAccessHotbar = quickAccessAthletes.length > 0;

  const tryoutPositionFilters = useMemo(() => {
    const seen = new Set<string>();
    const out: string[] = [];
    for (const raw of tryoutMeta.athletePositions) {
      const p = raw.trim();
      if (p.length > 0 && !seen.has(p)) {
        seen.add(p);
        out.push(p);
      }
    }
    return out;
  }, [tryoutMeta.athletePositions]);

  const filterOptions = useMemo<Array<{ label: string; value: FilterValue }>>(
    () => [
      { label: 'All', value: 'all' },
      ...tryoutPositionFilters.map((p) => ({ label: p, value: p })),
    ],
    [tryoutPositionFilters],
  );

  return (
    <div className="tryout-athletes-shell">
      <PageTopNav
        onBack={() => navigate('/demo/tryout')}
        backAriaLabel="Back to tryout dashboard"
        title={tryoutMeta.name}
      />
      <div
        ref={contentScrollRef}
        onScroll={handleContentScroll}
        className={`tryout-athletes-content${hasQuickAccessHotbar ? ' tryout-athletes-content--with-hotbar' : ''}`}
      >
        <AthleteSearchBar value={searchQuery} onChange={setSearchQuery} />

        {!isSearching ? (
          <div className="tryout-athletes-filter-row" role="group" aria-label="Filter by position">
            {filterOptions.map(({ label, value }) => (
              <button
                key={value}
                type="button"
                className={[
                  'tryout-athletes-filter-chip',
                  activeFilter === value ? 'tryout-athletes-filter-chip--active' : '',
                ].filter(Boolean).join(' ')}
                onClick={() => setActiveFilter(value)}
                aria-pressed={activeFilter === value}
              >
                {label}
              </button>
            ))}
          </div>
        ) : null}

        {filteredAthletes.length === 0 ? (
          <div className="tryout-athletes-empty">
            <p className="tryout-athletes-empty-title">No athletes match your search.</p>
          </div>
        ) : (
          <ul className="tryout-athletes-list" aria-label="Athletes">
            {filteredAthletes.map((athlete) => (
              <li key={athlete.id}>
                <AthleteCard
                  id={athlete.id}
                  name={athlete.name}
                  number={athlete.number}
                  position={athlete.position}
                  returningstatus={athlete.returningstatus}
                  isQuickAccess={quickAccessAthleteIdSet.has(athlete.id)}
                  imageThumbnailUrl={athlete.imageThumbnailUrl}
                  onClick={(id) => navigate(`/demo/tryout/athletes/${id}`)}
                />
              </li>
            ))}
          </ul>
        )}
      </div>
      {hasQuickAccessHotbar ? (
        <div className="athlete-quick-select-hotbar">
          <AthleteQuickSelectTabBar
            athletes={quickAccessAthletes}
            currentAthleteId=""
            onAthleteSelect={(id) => navigate(`/demo/tryout/athletes/${id}`)}
          />
        </div>
      ) : null}
      <ScrollToTopFab visible={scrollTopFabVisible} onPress={scrollContentToTop} />
    </div>
  );
};

export default DemoTryoutAthletesPage;
